import { getAppealLevelLabel } from "./appealCycle";

function flattenEmployees(employeesByBranch = {}) {
  if (Array.isArray(employeesByBranch)) return employeesByBranch;
  return Object.values(employeesByBranch).flat();
}

function findEmployee(employees, id) {
  if (!id) return null;
  return employees.find((e) => String(e.id) === String(id)) || null;
}

const LEVEL_REVIEWER_LABELS = {
  manager: "المدير المباشر",
  regionalManager: "مدير المنطقة",
  departmentManager: "مدير الإدارة",
  hr: "الموارد البشرية",
};

const LEVEL_ORDER = [
  "manager",
  "regionalManager",
  "departmentManager",
  "hr",
];

/**
 * Label while an appeal is still open.
 * After an intermediate accept: «مقبول من X — بانتظار Y»
 * Otherwise: «معلق عند Y»
 */
function buildOpenAppealStatusLabel(appeal, waitingOnName) {
  const waiting = waitingOnName || getAppealLevelLabel("manager", appeal);
  const approvedLevels = LEVEL_ORDER.filter(
    (level) => appeal?.levels?.[level]?.status === "approved",
  );
  if (approvedLevels.length === 0) {
    return `معلق عند ${waiting}`;
  }
  const lastApproved = approvedLevels[approvedLevels.length - 1];
  const fromLabel = getAppealLevelLabel(lastApproved, appeal) || lastApproved;
  return `مقبول من ${fromLabel} — بانتظار ${waiting}`;
}

function findOpenPenaltyAppeal(appeals = [], penalty) {
  if (!penalty?.id) return null;
  const penaltyId = String(penalty.id);
  return (
    appeals.find(
      (a) =>
        String(a.penaltyId) === penaltyId &&
        !a.finalStatus &&
        String(a.status || "").includes("pending"),
    ) ||
    appeals.find(
      (a) => String(a.penaltyId) === penaltyId && !a.finalStatus,
    ) ||
    null
  );
}

function reviewerNameFromEmployees(penalty, appeal, employees) {
  const employee = findEmployee(employees, penalty.employeeId);
  const level = appeal?.currentLevel || "manager";

  if (level === "manager") {
    const manager =
      findEmployee(employees, appeal?.managerId) ||
      findEmployee(employees, employee?.managerId);
    if (manager?.name && String(manager.id) !== String(penalty.employeeId)) {
      return manager.name;
    }
    if (
      employee?.managerName &&
      String(employee.managerId) !== String(penalty.employeeId)
    ) {
      return employee.managerName;
    }
    return LEVEL_REVIEWER_LABELS.manager;
  }

  if (level === "regionalManager") {
    const skip = findEmployee(employees, appeal?.skipManagerId);
    if (skip?.name && String(skip.id) !== String(penalty.employeeId)) {
      return skip.name;
    }
    if (appeal?.skipManagerName) {
      return appeal.skipManagerName;
    }
    const branchId = employee?.branchBackendId || employee?.branchId;
    const regional = employees.find(
      (e) =>
        (e.memberLevel === "regional-manager" ||
          e.roleSlugs?.includes("regional_manager")) &&
        e.status !== "terminated" &&
        String(e.id) !== String(penalty.employeeId) &&
        (e.managedBranchIds || []).some(
          (id) => String(id) === String(branchId),
        ),
    );
    return regional?.name || getAppealLevelLabel("regionalManager", appeal);
  }

  if (level === "departmentManager") {
    const dm = findEmployee(employees, employee?.departmentManagerId);
    if (dm?.name && String(dm.id) !== String(penalty.employeeId)) {
      return dm.name;
    }
    return getAppealLevelLabel("departmentManager", appeal);
  }

  return getAppealLevelLabel("hr", appeal);
}

/**
 * Status text for a penalty with an open appeal.
 * Before any accept: «معلق عند {المراجع}».
 * After intermediate accept: «مقبول من X — بانتظار Y».
 */
export function getAppealedPenaltyStatusLabel(
  penalty,
  appeals = [],
  employeesByBranch = {},
) {
  if (!penalty || penalty.status !== "appealed") {
    return penalty?.appealStatus || null;
  }

  const appeal = findOpenPenaltyAppeal(appeals, penalty);
  const employees = flattenEmployees(employeesByBranch);
  const appellantName =
    penalty.employeeName ||
    findEmployee(employees, penalty.employeeId)?.name ||
    null;

  const fromAppeal =
    appeal?.currentReviewerName ||
    (appeal?.currentLevel === "manager" ? appeal?.managerName : null);
  if (fromAppeal && fromAppeal !== appellantName) {
    return buildOpenAppealStatusLabel(appeal, fromAppeal);
  }

  const reviewerName = reviewerNameFromEmployees(penalty, appeal, employees);
  if (reviewerName && reviewerName !== appellantName) {
    return buildOpenAppealStatusLabel(appeal, reviewerName);
  }

  const levelLabel =
    getAppealLevelLabel(appeal?.currentLevel, appeal) ||
    LEVEL_REVIEWER_LABELS.manager;
  return buildOpenAppealStatusLabel(appeal, levelLabel);
}

/**
 * Enrich mapped penalties so appealStatus shows waiting / intermediate-accept labels.
 */
export function enrichPenaltiesWithAppealStatus(
  penalties = [],
  appeals = [],
  employeesByBranch = {},
) {
  return penalties.map((penalty) => {
    if (penalty.status !== "appealed") return penalty;
    const appeal = findOpenPenaltyAppeal(appeals, penalty);
    return {
      ...penalty,
      appealStatus: getAppealedPenaltyStatusLabel(
        penalty,
        appeals,
        employeesByBranch,
      ),
      appealDueAt: appeal?.hrDecisionDueAt || null,
    };
  });
}
